import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { getHotspotStyle } from '../../../utils/coordinateSystem';

const PreviewModal = ({ isOpen, onClose, pages, hotspots, catalogTitle }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showHotspots, setShowHotspots] = useState(true);
  const [isFlipping, setIsFlipping] = useState(false);
  const pageContainerRefs = useRef({});

  if (!isOpen) return null;

  const totalPages = pages?.length;

  // MAGAZINE LAYOUT LOGIC
  const isFrontCover = currentPage === 1;
  const isBackCover = currentPage === totalPages;
  const isSpread = !isFrontCover && !isBackCover;

  // Get pages to display
  const getDisplayPages = () => {
    if (isFrontCover) {
      return { left: null, right: pages?.find(p => p?.pageNumber === 1) };
    } else if (isBackCover) {
      return { left: pages?.find(p => p?.pageNumber === totalPages), right: null };
    } else {
      const leftPageNum = currentPage % 2 === 0 ? currentPage : currentPage - 1;
      const rightPageNum = leftPageNum + 1;
      return {
        left: pages?.find(p => p?.pageNumber === leftPageNum),
        right: rightPageNum <= totalPages ? pages?.find(p => p?.pageNumber === rightPageNum) : null
      };
    }
  };

  const { left: leftPageData, right: rightPageData } = getDisplayPages();

  const getPageHotspots = (pageNumber) => {
    return hotspots?.filter(h => h?.pageNumber === pageNumber && h?.visible) || [];
  };

  const handleHotspotClick = (hotspot) => {
    if (hotspot?.type === 'link' && hotspot?.url) {
      window.open(hotspot?.url, '_blank');
    } else if (hotspot?.type === 'buy' && hotspot?.sku) {
      alert(`Add to cart: ${hotspot?.sku}`);
    } else if (hotspot?.type === 'info') {
      alert(`${hotspot?.title}\n\n${hotspot?.description}`);
    }
  };

  const handlePrevPage = () => {
    if (isFlipping || currentPage === 1) return;
    
    setIsFlipping(true);
    setTimeout(() => {
      let prevPage;
      if (currentPage === 2) {
        prevPage = 1;
      } else if (isBackCover) {
        const lastSpreadStart = totalPages % 2 === 0 ? totalPages - 2 : totalPages - 1;
        prevPage = lastSpreadStart;
      } else {
        prevPage = Math.max(2, currentPage - 2);
      }
      setCurrentPage(prevPage);
      setIsFlipping(false);
    }, 300);
  };

  const handleNextPage = () => {
    if (isFlipping || currentPage === totalPages) return;
    
    setIsFlipping(true);
    setTimeout(() => {
      let nextPage;
      if (isFrontCover) {
        nextPage = 2;
      } else if (currentPage === totalPages - 1 || currentPage === totalPages - 2) {
        nextPage = totalPages;
      } else {
        nextPage = Math.min(totalPages, currentPage + 2);
      }
      setCurrentPage(nextPage);
      setIsFlipping(false);
    }, 300);
  };

  const renderPage = (pageData) => {
    if (!pageData) return null;

    const pageHotspots = getPageHotspots(pageData?.pageNumber);
    const pageKey = `page-${pageData?.pageNumber}`;

    return (
      <div 
        ref={(el) => { pageContainerRefs.current[pageKey] = el; }}
        className="relative w-full h-full bg-white shadow-elevation-xl rounded-lg overflow-hidden"
      >
        <Image
          src={pageData?.imageUrl || pageData?.thumbnailUrl || ''}
          alt={pageData?.imageAlt || `Preview of catalog page ${pageData?.pageNumber}`}
          className="w-full h-full object-cover"
        />
        {/* Hotspots */}
        {showHotspots && pageHotspots?.map((hotspot) => {
          const pageContainer = pageContainerRefs?.current?.[pageKey];
          const style = pageContainer
            ? getHotspotStyle(hotspot, pageContainer)
            : { left: '0px', top: '0px', width: '40px', height: '40px' };
          
          return (
            <button
              key={hotspot?.id}
              onClick={() => handleHotspotClick(hotspot)}
              className="absolute w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent hover:bg-accent/90 flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 shadow-elevation-md"
              style={{
                left: style?.left,
                top: style?.top,
                transform: 'translate(-50%, -50%)'
              }}
            >
              <Icon 
                name={hotspot?.type === 'buy' ? 'ShoppingCart' : hotspot?.type === 'link' ? 'Link' : 'Info'} 
                size={20} 
                className="text-accent-foreground"
              />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="w-full h-full max-w-7xl mx-auto flex flex-col p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              Preview Mode
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {catalogTitle}
            </p>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => setShowHotspots(!showHotspots)}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-base ${
                showHotspots
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-secondary text-secondary-foreground'
              }`}
            >
              <Icon name={showHotspots ? 'Eye' : 'EyeOff'} size={16} className="inline mr-2" />
              Hotspots
            </button>

            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary hover:bg-secondary/80 transition-base"
              aria-label="Close preview"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Flipbook Viewer */}
        <div className="flex-1 bg-muted/30 rounded-lg overflow-hidden flex items-center justify-center relative">
          <div className={`flex gap-2 ${isFrontCover || isBackCover ? 'max-w-2xl' : 'max-w-6xl'} w-full h-full p-4`}>
            {/* Left Page */}
            {leftPageData && (
              <div className="flex-1">
                {renderPage(leftPageData)}
              </div>
            )}

            {/* Right Page */}
            {rightPageData && (
              <div className="flex-1">
                {renderPage(rightPageData)}
              </div>
            )}
          </div>

          {/* Navigation */}
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1 || isFlipping}
            className="absolute left-4 md:left-8 w-12 h-12 md:w-14 md:h-14 rounded-full bg-card shadow-elevation-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent hover:text-accent-foreground transition-all duration-200"
            aria-label="Previous page"
          >
            <Icon name="ChevronLeft" size={24} />
          </button>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages || isFlipping}
            className="absolute right-4 md:right-8 w-12 h-12 md:w-14 md:h-14 rounded-full bg-card shadow-elevation-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent hover:text-accent-foreground transition-all duration-200"
            aria-label="Next page"
          >
            <Icon name="ChevronRight" size={24} />
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 md:mt-6">
          <div className="text-sm text-muted-foreground">
            {isFrontCover ? 'Front Cover' : isBackCover ? 'Back Cover' : `Pages ${leftPageData?.pageNumber}-${rightPageData?.pageNumber}`} ({currentPage} of {totalPages})
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
            <Button
              variant="default"
              iconName="Code"
              iconPosition="left"
              onClick={onClose}
            >
              Get Embed Code
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;